import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import type { ApiErrorResponse, MetricName, Metrics } from "../types";
import { formatLocalDate, normalizeDate } from "../utils/date";
import { METRIC_STALE_TIME } from "../constants";
import { DEFAULT_CAMPAIGN } from "../constants/campaignPerformance";
import { api } from "../utils/apiClient";

export type CampaignPerformanceRequest = {
  from: Date;
  to: Date;
  campaign: string;
  metrics: MetricName[];
  airlineCode?: string;
};

export type CampaignPerformanceResponse = {
  metrics: Metrics;
};

export type CampaignPerformanceQueryParams = {
  from: Date;
  to: Date;
  metrics: MetricName[];
  airlineCode?: string;
};

const CAMPAIGN_PERFORMANCE_ENDPOINT = "/api/v1/campaigns/performance";

const CAMPAIGN_PERFORMANCE_METRICS: MetricName[] = [
  "DISCOUNTED_BOOKINGS_SOLD",
  "AVERAGE_DISCOUNT_PER_BOOKING",
  "AVERAGE_DISCOUNT_PERCENTAGE_PER_BOOKING",
  "SEARCH_COVERAGE_PERCENTAGE",
];

export const fetchCampaignPerformance = async (
  endpoint: string,
  { to, from, campaign, metrics, airlineCode }: CampaignPerformanceRequest
): Promise<CampaignPerformanceResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.set("from", formatLocalDate(from));
  searchParams.set("to", formatLocalDate(to));
  searchParams.set("campaign", campaign);
  searchParams.set("metrics", metrics.join(","));
  if (airlineCode) {
    searchParams.set("airlineCode", airlineCode);
  }

  const res = await api.get(`${endpoint}?${searchParams.toString()}`);
  if (!res.ok) {
    let errorMessage = `Failed to load campaign performance: ${res.status}`;
    try {
      const body: ApiErrorResponse = await res.json();
      if (body?.error) {
        errorMessage = body.error;
      }
    } catch {
      // response body not parseable, use default message
    }
    throw new Error(errorMessage);
  }
  return res.json();
};

export const useCampaignPerformanceQuery = ({ from, to, metrics, airlineCode }: CampaignPerformanceQueryParams) => {
  const fromDate = normalizeDate(from);
  const toDate = normalizeDate(to);

  const request: CampaignPerformanceRequest = {
    from: fromDate,
    to: toDate,
    campaign: DEFAULT_CAMPAIGN,
    metrics: CAMPAIGN_PERFORMANCE_METRICS,
    airlineCode,
  };

  return useSuspenseQuery({
    queryKey: [
      CAMPAIGN_PERFORMANCE_ENDPOINT,
      fromDate.toISOString(),
      toDate.toISOString(),
      DEFAULT_CAMPAIGN,
      airlineCode,
    ],
    queryFn: () => fetchCampaignPerformance(CAMPAIGN_PERFORMANCE_ENDPOINT, request),
    select: (data) => data.metrics.filter((metric) => metrics.includes(metric.metadata.metricName)),
    staleTime: METRIC_STALE_TIME,
  });
};

export type MultiAirlineCampaignPerformanceQueryParams = {
  from: Date;
  to: Date;
  metrics: MetricName[];
  airlineCodes: string[];
};

export type AirlineMetrics = {
  airlineCode: string;
  metrics: Metrics;
};

export const useMultiAirlineCampaignPerformanceQuery = ({
  from,
  to,
  metrics,
  airlineCodes,
}: MultiAirlineCampaignPerformanceQueryParams): AirlineMetrics[] => {
  const { isAirlineUser } = useAuth();
  const fromDate = normalizeDate(from);
  const toDate = normalizeDate(to);

  return useSuspenseQueries({
    queries: airlineCodes.map((airlineCode) => {
      const endpoint = isAirlineUser
        ? CAMPAIGN_PERFORMANCE_ENDPOINT
        : `${CAMPAIGN_PERFORMANCE_ENDPOINT}/airlines/${airlineCode}`;
      const request: CampaignPerformanceRequest = {
        from: fromDate,
        to: toDate,
        campaign: DEFAULT_CAMPAIGN,
        metrics: CAMPAIGN_PERFORMANCE_METRICS,
        airlineCode: isAirlineUser ? airlineCode : undefined,
      };

      return {
        queryKey: [endpoint, fromDate.toISOString(), toDate.toISOString(), DEFAULT_CAMPAIGN, airlineCode],
        queryFn: () => fetchCampaignPerformance(endpoint, request),
        staleTime: METRIC_STALE_TIME,
      };
    }),
    combine: (results) =>
      results.map((result, index) => ({
        airlineCode: airlineCodes[index],
        metrics: result.data.metrics.filter((metric) => metrics.includes(metric.metadata.metricName)),
      })),
  });
};
